import React, { useState, useEffect, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import UserSearchDropdown from '../components/UserSearchDropdown';
import { toast } from '../components/Toast/ToastContainer';
import '../styles/TwitterTools.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const TwitterWinnerPicker = () => {
    const { isAuthenticated, user } = useContext(AuthContext);
    const [tweetId, setTweetId] = useState('');
    const [numberOfWinners, setNumberOfWinners] = useState(1);
    const [expectedEntries, setExpectedEntries] = useState('');
    const [loading, setLoading] = useState(false);
    const [checkingCredits, setCheckingCredits] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [credits, setCredits] = useState(0);
    const [creditCheck, setCreditCheck] = useState(null);
    const [giveawayHistory, setGiveawayHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [verificationId, setVerificationId] = useState('');
    const [verificationResult, setVerificationResult] = useState(null);
    const [verifyingRoll, setVerifyingRoll] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const [recentGiveaways, setRecentGiveaways] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [showRecentGiveaways, setShowRecentGiveaways] = useState(false);
    const [recentPage, setRecentPage] = useState(1);
    const [recentTotalPages, setRecentTotalPages] = useState(1);
    const [showRerollModal, setShowRerollModal] = useState(false);
    const [rerollReason, setRerollReason] = useState('');
    const [rerolling, setRerolling] = useState(false);
    // Referral system state
    const [referralCode, setReferralCode] = useState('');
    const [referralInput, setReferralInput] = useState('');
    const [referralStats, setReferralStats] = useState({
        totalReferrals: 0,
        qualifiedReferrals: 0,
        rewardsEarned: 0
    });
    const [referredBy, setReferredBy] = useState(null);
    const [applyingReferral, setApplyingReferral] = useState(false);
    const [referralMessage, setReferralMessage] = useState({ type: '', text: '' });
    const [copiedReferral, setCopiedReferral] = useState(false);
    // Advanced filters state
    const [mustFollow, setMustFollow] = useState([]);
    const [mustFollowInput, setMustFollowInput] = useState('');
    const [enforceMustFollow, setEnforceMustFollow] = useState(false);
    const [requireReply, setRequireReply] = useState(false);
    const [requireQuote, setRequireQuote] = useState(false);
    const [requirePfp, setRequirePfp] = useState(false);
    const [requireBanner, setRequireBanner] = useState(false);
    const [requireLocation, setRequireLocation] = useState(false);
    const [requireDescription, setRequireDescription] = useState(false);
    const [minTweetCount, setMinTweetCount] = useState('');
    const [accountOlderThanDays, setAccountOlderThanDays] = useState('');
    const [latestTweetWithinDays, setLatestTweetWithinDays] = useState('');
    const [minFollowers, setMinFollowers] = useState('');
    // Exclude previous winners state
    const [excludePreviousWinners, setExcludePreviousWinners] = useState(false);
    const [excludeWinnersDays, setExcludeWinnersDays] = useState(7);
    // Gift credits modal state
    const [showGiftModal, setShowGiftModal] = useState(false);
    const [giftRecipient, setGiftRecipient] = useState('');
    const [giftAmount, setGiftAmount] = useState('');
    const [giftingCredits, setGiftingCredits] = useState(false);
    const [giftMessage, setGiftMessage] = useState({ type: '', text: '' });

    const handleVerifyRoll = async (e) => {
        e.preventDefault();
        if (!verificationId.trim()) {
            setVerificationError('Please enter a verification ID');
            return;
        }

        setVerifyingRoll(true);
        setVerificationError('');
        setVerificationResult(null);

        try {
            const response = await axios.get(`/api/twitter-verification/verify/${verificationId.trim()}`);
            if (response.data.success) {
                setVerificationResult(response.data.data);
            }
        } catch (err) {
            setVerificationError(err.response?.data?.message || 'Failed to verify giveaway roll');
        } finally {
            setVerifyingRoll(false);
        }
    };

    const fetchGiveawayHistory = async () => {
        if (!isAuthenticated) return;
        
        setLoadingHistory(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/twitter-verification/giveaway-history', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setGiveawayHistory(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch giveaway history:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const fetchRecentGiveaways = async (page = 1) => {
        setLoadingRecent(true);
        try {
            const response = await axios.get(`/api/twitter-verification/recent-giveaways?limit=5&page=${page}`);
            if (response.data.success) {
                setRecentGiveaways(response.data.data);
                setRecentTotalPages(response.data.pagination.pages);
                setRecentPage(page);
            }
        } catch (err) {
            console.error('Failed to fetch recent giveaways:', err);
        } finally {
            setLoadingRecent(false);
        }
    };

    const handlePickWinner = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            setError('Please login to use the Twitter Winner Picker');
            return;
        }

        if (!expectedEntries || expectedEntries < 1) {
            setError('Please enter the expected number of entries first');
            return;
        }

        setError('');
        setResult(null);
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/twitter-verification/pick-winner', {
                tweetId,
                numberOfWinners: parseInt(numberOfWinners),
                expectedEntries: parseInt(expectedEntries),
                excludePreviousWinners,
                excludeWinnersDays: parseInt(excludeWinnersDays),
                filters: {
                    mustFollowUsernames: mustFollow.map(username => username.replace(/^@/, '')),
                    enforceMustFollow: enforceMustFollow && mustFollow.length > 0,
                    requireReply: requireReply,
                    requireQuote: requireQuote,
                    requireProfileImage: requirePfp,
                    requireBanner: requireBanner,
                    requireLocation: requireLocation,
                    requireDescription: requireDescription,
                    minTweetCount: minTweetCount !== '' ? parseInt(minTweetCount, 10) : undefined,
                    accountOlderThanDays: accountOlderThanDays !== '' ? parseInt(accountOlderThanDays, 10) : undefined,
                    latestTweetWithinDays: latestTweetWithinDays !== '' ? parseInt(latestTweetWithinDays, 10) : undefined,
                    minFollowers: minFollowers !== '' ? parseInt(minFollowers, 10) : undefined
                }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setResult(response.data);
                // Refresh credit balance and history
                await fetchCreditBalance();
                await fetchGiveawayHistory();
            } else {
                setError(response.data.message || 'Failed to pick winners');
            }
        } catch (err) {
            if (err.response?.status === 402) {
                setError(err.response.data.message || 'Insufficient credits');
            } else {
                setError(err.response?.data?.message || 'An error occurred while picking winners. Please try again.');
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleReroll = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            setError('Please login to reroll giveaways');
            return;
        }

        if (!result?.verificationId) {
            setError('No giveaway to reroll');
            return;
        }

        if (!rerollReason.trim()) {
            setError('Please provide a reason for rerolling');
            return;
        }

        setError('');
        setRerolling(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/twitter-verification/reroll', {
                verificationId: result.verificationId,
                rerollReason: rerollReason.trim(),
                excludePreviousWinners,
                excludeWinnersDays: parseInt(excludeWinnersDays)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                // Update the result with the new roll
                setResult({
                    ...response.data,
                    pickedAt: new Date()
                });
                setShowRerollModal(false);
                setRerollReason('');
                // Refresh history
                await fetchGiveawayHistory();
            } else {
                setError(response.data.message || 'Failed to reroll giveaway');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while rerolling. Please try again.');
            console.error(err);
        } finally {
            setRerolling(false);
        }
    };

    const extractTweetId = (input) => {
        // Extract tweet ID from URL or use as-is if it's already an ID
        const urlMatch = input.match(/status\/(\d+)/);
        return urlMatch ? urlMatch[1] : input;
    };

    const handleTweetIdChange = (e) => {
        const input = e.target.value;
        setTweetId(extractTweetId(input));
    };

    // Fetch credit balance
    useEffect(() => {
        if (isAuthenticated) {
            fetchCreditBalance();
            fetchReferralStats();
        }
    }, [isAuthenticated]);

    // Fetch recent giveaways on component mount
    useEffect(() => {
        fetchRecentGiveaways(1);
    }, []);

    const fetchCreditBalance = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/credits/balance', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCredits(response.data.credits || 0);
            }
        } catch (error) {
            console.error('Error fetching credit balance:', error);
        }
    };

    const fetchReferralStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/referrals/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setReferralCode(response.data.data.referralCode);
                setReferredBy(response.data.data.referredBy);
                setReferralStats(response.data.data.stats);
            }
        } catch (error) {
            console.error('Error fetching referral stats:', error);
        }
    };

    const handleApplyReferral = async () => {
        if (!referralInput.trim()) {
            setReferralMessage({ type: 'error', text: 'Please enter a referral code' });
            return;
        }

        setApplyingReferral(true);
        setReferralMessage({ type: '', text: '' });

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/referrals/apply', 
                { referralCode: referralInput.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setReferralMessage({ type: 'success', text: response.data.message });
                setReferralInput('');
                await fetchReferralStats(); // Refresh stats
            }
        } catch (error) {
            setReferralMessage({ 
                type: 'error', 
                text: error.response?.data?.message || 'Failed to apply referral code' 
            });
        } finally {
            setApplyingReferral(false);
        }
    };

    const handleCopyReferral = () => {
        navigator.clipboard.writeText(referralCode);
        setCopiedReferral(true);
        setTimeout(() => setCopiedReferral(false), 2000);
    };

    // Handle gifting credits
    const handleGiftCredits = async (e) => {
        e.preventDefault();
        if (!giftRecipient.trim() || !giftAmount || giftAmount <= 0) {
            setGiftMessage({ type: 'error', text: 'Please enter a valid recipient and amount' });
            return;
        }

        setGiftingCredits(true);
        setGiftMessage({ type: '', text: '' });

        try {
            const response = await axios.post('/api/credits/gift', {
                recipientUsername: giftRecipient.trim(),
                credits: parseInt(giftAmount)
            });

            setGiftMessage({ type: 'success', text: response.data.message });
            setCredits(response.data.sender.remainingCredits);
            setGiftRecipient('');
            setGiftAmount('');
            setTimeout(() => {
                setShowGiftModal(false);
                setGiftMessage({ type: '', text: '' });
            }, 2000);
        } catch (error) {
            setGiftMessage({ 
                type: 'error', 
                text: error.response?.data?.message || 'Failed to gift credits' 
            });
        } finally {
            setGiftingCredits(false);
        }
    };

    // Calculate credits needed (15.27 credits per entry + premium features)
    const calculateCreditsNeeded = (entries) => {
        let baseCredits = Math.ceil(entries * 15.27);
        let premiumCredits = 0;
        
        // Must-follow verification: 100 credits per entry per username
        if (enforceMustFollow && mustFollow.length > 0) {
            premiumCredits += entries * mustFollow.length * 100;
        }
        
        // Reply checking: 15 credits per entry
        if (requireReply) {
            premiumCredits += entries * 15;
        }
        
        // Quote checking: 15 credits per entry
        if (requireQuote) {
            premiumCredits += entries * 15;
        }
        
        return baseCredits + premiumCredits;
    };

    // Check credits before rolling
    const handleCheckCredits = async () => {
        if (!expectedEntries || expectedEntries < 1) {
            setError('Please enter a valid number of expected entries');
            return;
        }

        setCheckingCredits(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/credits/check', 
                { 
                    entries: parseInt(expectedEntries),
                    filters: {
                        mustFollowUsernames: mustFollow,
                        enforceMustFollow: enforceMustFollow && mustFollow.length > 0,
                        requireReply: requireReply,
                        requireQuote: requireQuote
                    }
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                setCreditCheck(response.data);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to check credits');
        } finally {
            setCheckingCredits(false);
        }
    };

    const copyWinners = () => {
        const winnersText = result.winners.map((w, i) =>
            `${i + 1}. @${w.username} - ${w.displayName}`
        ).join('\n');
        navigator.clipboard.writeText(winnersText);
    };

    // Chart Data and Configuration
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: '#e2e8f0', // Light text for dark mode
                }
            },
            title: {
                display: true,
                text: 'Credit Usage & Cost per Giveaway Entries',
                color: '#e2e8f0',
                font: {
                    size: 16
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            if (context.datasetIndex === 1) {
                                label += context.parsed.y.toLocaleString() + ' Credits';
                            } else if (context.datasetIndex === 0) {
                                label = 'Cost: $' + context.parsed.y.toFixed(2);
                            }
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: { color: '#94a3b8' },
                grid: { color: '#334155' }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                ticks: { color: '#94a3b8' },
                grid: { color: '#334155' },
                title: { display: true, text: 'Cost ($)', color: '#94a3b8' }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                ticks: { color: '#94a3b8' },
                title: { display: true, text: 'Credits Needed', color: '#94a3b8' }
            },
        },
    };

    const labels = ['50 Entries', '100 Entries', '200 Entries', '500 Entries', '750+ Entries', '1000 Entries'];

    // Pricing model based on actual TwitterAPI.io usage data:
    // - Real usage: 550 entries used 8,400 credits
    // - Credits per entry: 8,400 / 550 ≈ 15.27 credits per entry
    // - User's cost: 1,000,000 credits = 1000rs
    // - Cost per credit: 1000rs / 1,000,000 = 0.001rs per credit
    // - Converting to USD (~83 INR = 1 USD): 0.001rs ≈ $0.00001205 per credit
    // Pricing structure (30 giveaways of 500 entries = $5):
    // - 50 entries: 764 credits ≈ $0.0167
    // - 100 entries: 1,527 credits ≈ $0.0333
    // - 200 entries: 3,054 credits ≈ $0.0667
    // - 500 entries: 7,635 credits ≈ $0.1667
    // - 750 entries: 11,453 credits ≈ $0.2500
    // - 1,000 entries: 15,270 credits ≈ $0.3335
    const chartData = {
        labels,
        datasets: [
            {
                label: 'Est. Cost ($)',
                data: [0.0167, 0.0333, 0.0667, 0.1667, 0.2500, 0.3335],
                backgroundColor: 'rgba(53, 162, 235, 0.7)',
                yAxisID: 'y',
            },
            {
                label: 'Credits Required',
                data: [764, 1527, 3054, 7635, 11453, 15270],
                backgroundColor: 'rgba(255, 99, 132, 0.7)',
                yAxisID: 'y1',
            },
        ],
    };

    return (
        <div className="twitter-tools-container">
            <div className="tools-header">
                <h1>🎉 Twitter Winner Picker</h1>
                <p>Randomly select winners from tweet retweets for giveaways and contests</p>
            </div>

            <div style={{
                backgroundColor: 'rgba(220, 38, 38, 0.15)',
                border: '1px solid #dc2626',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '2rem',
                color: '#fca5a5',
                textAlign: 'center',
                fontSize: '1.1rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.1), 0 2px 4px -1px rgba(220, 38, 38, 0.06)'
            }}>
                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                <span>
                    <strong style={{ color: '#ef4444' }}>Credits are NON-REFUNDABLE.</strong> If you want to test the feature, please purchase only the minimal, i.e the $1 pack to test.
                </span>
            </div>

            {/* Referral System Section */}
            {isAuthenticated && (
                <div style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.15)',
                    border: '2px solid rgba(34, 197, 94, 0.4)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                    boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.1), 0 2px 4px -1px rgba(34, 197, 94, 0.06)'
                }}>
                    <h3 style={{ 
                        color: '#22c55e', 
                        marginTop: 0, 
                        marginBottom: '1rem',
                        fontSize: '1.4rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <span>🎁</span> Referral Rewards
                    </h3>

                    {/* Your Referral Code */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ 
                            display: 'block', 
                            color: '#86efac', 
                            fontSize: '0.95rem',
                            marginBottom: '0.5rem',
                            fontWeight: '500'
                        }}>
                            Your Referral Code
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input
                                type="text"
                                value={referralCode}
                                readOnly
                                style={{
                                    flex: 1,
                                    padding: '0.75rem 1rem',
                                    backgroundColor: 'rgba(22, 163, 74, 0.1)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '8px',
                                    color: '#22c55e',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    fontFamily: 'monospace'
                                }}
                            />
                            <button
                                onClick={handleCopyReferral}
                                style={{
                                    padding: '0.75rem 1.25rem',
                                    backgroundColor: copiedReferral ? 'rgba(34, 197, 94, 0.9)' : 'rgba(34, 197, 94, 0.7)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {copiedReferral ? '✓ Copied!' : '📋 Copy'}
                            </button>
                        </div>
                        <p style={{ 
                            fontSize: '0.85rem', 
                            color: '#86efac', 
                            marginTop: '0.5rem',
                            marginBottom: 0
                        }}>
                            Share this code with friends! When they buy a $10 pack, you earn a $1 pack (28,448 credits) FREE! 🎉
                        </p>
                    </div>

                    {/* Enter Referral Code (if not already referred) */}
                    {!referredBy && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ 
                                display: 'block', 
                                color: '#86efac', 
                                fontSize: '0.95rem',
                                marginBottom: '0.5rem',
                                fontWeight: '500'
                            }}>
                                Have a Referral Code? (One-time only)
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    type="text"
                                    value={referralInput}
                                    onChange={(e) => setReferralInput(e.target.value)}
                                    placeholder="Enter referral code..."
                                    disabled={applyingReferral}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem 1rem',
                                        backgroundColor: 'rgba(22, 163, 74, 0.1)',
                                        border: '1px solid rgba(34, 197, 94, 0.3)',
                                        borderRadius: '8px',
                                        color: '#d1fae5',
                                        fontSize: '1rem'
                                    }}
                                />
                                <button
                                    onClick={handleApplyReferral}
                                    disabled={applyingReferral}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: applyingReferral ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.7)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: applyingReferral ? 'not-allowed' : 'pointer',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {applyingReferral ? 'Applying...' : 'Apply'}
                                </button>
                            </div>
                            {referralMessage.text && (
                                <p style={{ 
                                    marginTop: '0.5rem', 
                                    marginBottom: 0,
                                    color: referralMessage.type === 'success' ? '#22c55e' : '#fca5a5',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}>
                                    {referralMessage.text}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Referred By Display */}
                    {referredBy && (
                        <div style={{ 
                            marginBottom: '1.5rem',
                            padding: '0.75rem 1rem',
                            backgroundColor: 'rgba(22, 163, 74, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '8px'
                        }}>
                            <span style={{ color: '#86efac', fontSize: '0.9rem' }}>
                                ✓ Referred by: <strong style={{ color: '#22c55e' }}>{referredBy}</strong>
                            </span>
                        </div>
                    )}

                    {/* Referral Stats */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                        gap: '1rem'
                    }}>
                        <div style={{
                            padding: '1rem',
                            backgroundColor: 'rgba(22, 163, 74, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '12px',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '1.75rem', color: '#22c55e', fontWeight: 'bold' }}>
                                {referralStats.totalReferrals}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#86efac', marginTop: '0.25rem' }}>
                                Total Referrals
                            </div>
                        </div>
                        <div style={{
                            padding: '1rem',
                            backgroundColor: 'rgba(22, 163, 74, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '12px',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '1.75rem', color: '#22c55e', fontWeight: 'bold' }}>
                                {referralStats.qualifiedReferrals}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#86efac', marginTop: '0.25rem' }}>
                                Qualified ($10 Pack)
                            </div>
                        </div>
                        <div style={{
                            padding: '1rem',
                            backgroundColor: 'rgba(22, 163, 74, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '12px',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '1.75rem', color: '#22c55e', fontWeight: 'bold' }}>
                                {referralStats.rewardsEarned.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#86efac', marginTop: '0.25rem' }}>
                                Credits Earned
                            </div>
                        </div>
                    </div>

                    <p style={{ 
                        fontSize: '0.8rem', 
                        color: '#86efac', 
                        marginTop: '1rem',
                        marginBottom: 0,
                        textAlign: 'center',
                        fontStyle: 'italic'
                    }}>
                        💡 Earn 28,448 credits for each friend who purchases the $10 pack using your code!
                    </p>
                </div>
            )}

            {/* Credit Balance Display */}
            {isAuthenticated && (
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem 1.5rem',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div>
                        <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                            Your Credit Balance
                        </div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#3b82f6' }}>
                            {credits.toLocaleString()} Credits
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                            onClick={() => {
                                setShowHistory(!showHistory);
                                if (!showHistory && giveawayHistory.length === 0) {
                                    fetchGiveawayHistory();
                                }
                            }}
                            style={{
                                padding: '0.5rem 1rem',
                                background: '#8b5cf6',
                                color: 'white',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            {showHistory ? '📊 Hide History' : '📜 View History'}
                        </button>
                        <button 
                            onClick={() => setShowGiftModal(true)}
                            style={{
                                padding: '0.5rem 1rem',
                                background: '#10b981',
                                color: 'white',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            🎁 Gift Credits
                        </button>
                        <a 
                            href="#purchase-credits" 
                            style={{
                                padding: '0.5rem 1rem',
                                background: '#3b82f6',
                                color: 'white',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                fontWeight: '600'
                            }}
                        >
                            Buy Credits
                        </a>
                    </div>
                </div>
            )}

            {/* Recently Rolled Giveaways Dropdown */}
            <div style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                background: 'var(--card-bg, #1e293b)',
                borderRadius: '16px',
                border: '1px solid var(--border-color, #334155)'
            }}>
                <div
                    onClick={() => {
                        setShowRecentGiveaways(!showRecentGiveaways);
                        if (!showRecentGiveaways && recentGiveaways.length === 0) {
                            fetchRecentGiveaways(1);
                        }
                    }}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '0.5rem 0'
                    }}
                >
                    <h2 style={{ margin: 0, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🎉 Recently Rolled Giveaways
                    </h2>
                    <span style={{
                        color: '#94a3b8',
                        fontSize: '1.2rem',
                        transform: showRecentGiveaways ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>

                {showRecentGiveaways && (
                    <div style={{ marginTop: '1rem' }}>
                        {loadingRecent ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                Loading recent giveaways...
                            </div>
                        ) : recentGiveaways.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                No recent giveaways found.
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                                    {recentGiveaways.map((roll, index) => (
                                        <div key={roll._id} style={{
                                            padding: '1rem',
                                            background: 'rgba(30, 41, 59, 0.5)',
                                            borderRadius: '12px',
                                            border: '1px solid #475569'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                                                        <strong>Rolled by:</strong> {roll.userId?.username || 'Unknown'}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                                                        {new Date(roll.rolledAt).toLocaleString()}
                                                    </div>
                                                </div>
                                                <a
                                                    href={`https://x.com/i/web/status/${roll.tweetId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        padding: '0.25rem 0.75rem',
                                                        background: '#3b82f6',
                                                        color: 'white',
                                                        borderRadius: '6px',
                                                        textDecoration: 'none',
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    View Tweet
                                                </a>
                                            </div>

                                            {roll.winners && roll.winners.length > 0 && (
                                                <div>
                                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                                                        Winner{roll.winners.length > 1 ? 's' : ''} with verification ID:
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                        {roll.winners.map((winner, i) => (
                                                            <div key={i} style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.5rem 0.75rem',
                                                                background: 'rgba(59, 130, 246, 0.1)',
                                                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                                                borderRadius: '8px'
                                                            }}>
                                                                {winner.profileImageUrl && (
                                                                    <img
                                                                        src={winner.profileImageUrl}
                                                                        alt={winner.displayName}
                                                                        style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                                                                    />
                                                                )}
                                                                <span style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                                                    @{winner.username}
                                                                </span>
                                                                {winner.isVerified && (
                                                                    <span style={{ color: '#3b82f6' }}>✓</span>
                                                                )}
                                                                <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '0.5rem' }}>
                                                                    ID: {roll.verificationId}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination Controls */}
                                {recentTotalPages > 1 && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        marginTop: '1rem',
                                        paddingTop: '1rem',
                                        borderTop: '1px solid #475569'
                                    }}>
                                        <button
                                            onClick={() => fetchRecentGiveaways(recentPage - 1)}
                                            disabled={recentPage <= 1}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: recentPage <= 1 ? '#475569' : '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: recentPage <= 1 ? 'not-allowed' : 'pointer',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Previous
                                        </button>

                                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                            Page {recentPage} of {recentTotalPages}
                                        </span>

                                        <button
                                            onClick={() => fetchRecentGiveaways(recentPage + 1)}
                                            disabled={recentPage >= recentTotalPages}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: recentPage >= recentTotalPages ? '#475569' : '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: recentPage >= recentTotalPages ? 'not-allowed' : 'pointer',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Giveaway History Section */}
            {isAuthenticated && showHistory && (
                <div style={{
                    marginBottom: '2rem',
                    padding: '1.5rem',
                    background: 'var(--card-bg, #1e293b)',
                    borderRadius: '16px',
                    border: '1px solid var(--border-color, #334155)'
                }}>
                    <h2 style={{ marginBottom: '1rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        📜 Your Giveaway History
                    </h2>
                    
                    {loadingHistory ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                            Loading history...
                        </div>
                    ) : giveawayHistory.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                            No giveaway history yet. Roll your first winner to see it here!
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {giveawayHistory.map((roll, index) => (
                                <div key={roll._id} style={{
                                    padding: '1rem',
                                    background: 'rgba(30, 41, 59, 0.5)',
                                    borderRadius: '12px',
                                    border: '1px solid #475569'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                                        <div style={{ flex: 1 }}>
                                            {roll.verificationId && (
                                                <div style={{ 
                                                    marginBottom: '0.5rem',
                                                    padding: '0.5rem 0.75rem',
                                                    background: 'rgba(16, 185, 129, 0.1)',
                                                    border: '1px solid rgba(16, 185, 129, 0.3)',
                                                    borderRadius: '8px',
                                                    display: 'inline-block'
                                                }}>
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b', marginRight: '0.5rem' }}>
                                                        🔐 Verification ID:
                                                    </span>
                                                    <span style={{ 
                                                        fontSize: '0.85rem', 
                                                        color: '#10b981', 
                                                        fontFamily: 'monospace',
                                                        fontWeight: '600'
                                                    }}>
                                                        {roll.verificationId}
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(roll.verificationId);
                                                            toast.success('Verification ID copied!');
                                                        }}
                                                        style={{
                                                            marginLeft: '0.5rem',
                                                            padding: '0.15rem 0.4rem',
                                                            background: '#10b981',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        Copy
                                                    </button>
                                                    <a
                                                        href={`/verify?id=${roll.verificationId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            marginLeft: '0.5rem',
                                                            padding: '0.15rem 0.4rem',
                                                            background: '#3b82f6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.7rem',
                                                            textDecoration: 'none',
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        View
                                                    </a>
                                                </div>
                                            )}
                                            <div style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                                                <strong>Tweet ID:</strong> {roll.tweetId}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                                                {new Date(roll.rolledAt).toLocaleString()} 
                                                {roll.fromCache && <span style={{ marginLeft: '0.5rem', color: '#10b981' }}>⚡ From Cache</span>}
                                                {roll.rerollReason && <span style={{ marginLeft: '0.5rem', color: '#fbbf24' }}>🔄 Rerolled</span>}
                                            </div>
                                            {roll.rerollReason && (
                                                <div style={{ fontSize: '0.8rem', color: '#fbbf24', marginTop: '0.25rem' }}>
                                                    Reason: {roll.rerollReason}
                                                </div>
                                            )}
                                        </div>
                                        <a 
                                            href={`https://x.com/i/web/status/${roll.tweetId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                padding: '0.25rem 0.75rem',
                                                background: '#3b82f6',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            View Tweet
                                        </a>
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                                        <div style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                            🔄 <strong>{roll.totalRetweeters}</strong> entries
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                            🏆 <strong>{roll.numberOfWinners}</strong> winner{roll.numberOfWinners > 1 ? 's' : ''}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: roll.creditsUsed > 0 ? '#ef4444' : '#10b981' }}>
                                            💳 <strong>{roll.creditsUsed.toLocaleString()}</strong> credits
                                        </div>
                                    </div>
                                    
                                    {roll.winners && roll.winners.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                                                Winners:
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {roll.winners.map((winner, i) => (
                                                    <div key={i} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        padding: '0.5rem 0.75rem',
                                                        background: 'rgba(59, 130, 246, 0.1)',
                                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                                        borderRadius: '8px'
                                                    }}>
                                                        {winner.profileImageUrl && (
                                                            <img 
                                                                src={winner.profileImageUrl} 
                                                                alt={winner.displayName}
                                                                style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                                                            />
                                                        )}
                                                        <span style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                                            @{winner.username}
                                                        </span>
                                                        {winner.isVerified && (
                                                            <span style={{ color: '#3b82f6' }}>✓</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Purchase Credits Section */}
            <div id="purchase-credits" style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                background: 'var(--card-bg, #1e293b)',
                borderRadius: '16px',
                border: '1px solid var(--border-color, #334155)'
            }}>
                <h2 style={{ marginBottom: '1rem', color: '#e2e8f0' }}>💰 Purchase Credits</h2>
                
                {/* Credits Per Tier */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#cbd5e1' }}>
                        📊 Base Credits per Giveaway Tier
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '0.75rem',
                        marginBottom: '1rem'
                    }}>
                        {[
                            { entries: '50', credits: 764 },
                            { entries: '100', credits: 1527 },
                            { entries: '200', credits: 3054 },
                            { entries: '500', credits: 7635 },
                            { entries: '750', credits: 11453 },
                            { entries: '1,000', credits: 15270 }
                        ].map(({ entries, credits }) => (
                            <div key={entries} style={{
                                padding: '0.75rem',
                                border: '1px solid #475569',
                                borderRadius: '8px',
                                textAlign: 'center',
                                background: 'rgba(71, 85, 105, 0.1)'
                            }}>
                                <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                                    {entries} entries
                                </div>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#3b82f6' }}>
                                    {credits.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                    credits
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Premium Features Pricing */}
                    <details style={{
                        background: 'rgba(168, 85, 247, 0.1)',
                        border: '1px solid rgba(168, 85, 247, 0.3)',
                        borderRadius: '12px',
                        padding: '1rem',
                        marginTop: '1rem'
                    }}>
                        <summary style={{
                            cursor: 'pointer',
                            fontWeight: '600',
                            color: '#a78bfa',
                            fontSize: '1rem',
                            marginBottom: '0.5rem',
                            userSelect: 'none'
                        }}>
                            ⚡ Premium Features Pricing (Optional)
                        </summary>
                        <div style={{ marginTop: '1rem', color: '#cbd5e1', fontSize: '0.9rem' }}>
                            <p style={{ marginBottom: '0.75rem' }}>Premium features are charged ONLY when enabled:</p>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div style={{
                                    padding: '0.75rem',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(59, 130, 246, 0.2)'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#60a5fa', marginBottom: '0.25rem' }}>✓ Enforce Must-Follow</div>
                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>+100 credits per entry per @handle</div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Example: 100 entries × 1 handle = +10,000 credits</div>
                                </div>
                                
                                <div style={{
                                    padding: '0.75rem',
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(34, 197, 94, 0.2)'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#4ade80', marginBottom: '0.25rem' }}>💬 Require Reply</div>
                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>+15 credits per entry</div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Example: 100 entries = +1,500 credits (~2x cost)</div>
                                </div>
                                
                                <div style={{
                                    padding: '0.75rem',
                                    background: 'rgba(245, 158, 11, 0.1)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(245, 158, 11, 0.2)'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#fbbf24', marginBottom: '0.25rem' }}>🔄 Require Quote Tweet</div>
                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>+15 credits per entry</div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>Example: 100 entries = +1,500 credits (~2x cost)</div>
                                </div>
                            </div>
                            
                            <div style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                background: 'rgba(239, 68, 68, 0.1)',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                color: '#fca5a5'
                            }}>
                                ⚠️ <strong>Note:</strong> Using all premium features together can increase costs by 10-20x. Use selectively!
                            </div>
                        </div>
                    </details>
                </div>

                {/* Purchase Packages */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#cbd5e1' }}>
                        🎁 Credit Packages
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '1rem',
                        marginBottom: '1rem'
                    }}>
                        {[
                            { amount: 1, net: 0.621, credits: 28448 },
                            { amount: 3, net: 2.463, credits: 112810 },
                            { amount: 5, net: 4.305, credits: 197207 },
                            { amount: 10, net: 8.91, credits: 408167 }
                        ].map(({ amount, net, credits }) => (
                            <div key={amount} style={{
                                padding: '1rem',
                                border: '2px solid #334155',
                                borderRadius: '12px',
                                textAlign: 'center',
                                background: 'rgba(59, 130, 246, 0.05)'
                            }}>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                    ${amount}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                                    ~{credits.toLocaleString()} Credits
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                                    (Net: ${net.toFixed(2)} after fees)
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* What You Can Do */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#cbd5e1' }}>
                        🎯 What You Can Do with Each Package
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '1rem'
                    }}>
                        {[
                            {
                                package: '$1 (28,448 credits)',
                                items: [
                                    { tier: '50+', count: 37 },
                                    { tier: '100+', count: 18 },
                                    { tier: '200+', count: 9 },
                                    { tier: '500+', count: 3 },
                                    { tier: '750+', count: 2 },
                                    { tier: '1,000+', count: 1 }
                                ]
                            },
                            {
                                package: '$3 (112,810 credits)',
                                items: [
                                    { tier: '50+', count: 147 },
                                    { tier: '100+', count: 73 },
                                    { tier: '200+', count: 36 },
                                    { tier: '500+', count: 14 },
                                    { tier: '750+', count: 9 },
                                    { tier: '1,000+', count: 7 }
                                ]
                            },
                            {
                                package: '$5 (197,207 credits)',
                                items: [
                                    { tier: '50+', count: 258 },
                                    { tier: '100+', count: 129 },
                                    { tier: '200+', count: 64 },
                                    { tier: '500+', count: 25 },
                                    { tier: '750+', count: 17 },
                                    { tier: '1,000+', count: 12 }
                                ]
                            },
                            {
                                package: '$10 (408,167 credits)',
                                items: [
                                    { tier: '50+', count: 534 },
                                    { tier: '100+', count: 267 },
                                    { tier: '200+', count: 133 },
                                    { tier: '500+', count: 53 },
                                    { tier: '750+', count: 35 },
                                    { tier: '1,000+', count: 26 }
                                ]
                            }
                        ].map(({ package: pkg, items }, idx) => (
                            <div key={idx} style={{
                                padding: '1rem',
                                border: '1px solid #475569',
                                borderRadius: '12px',
                                background: 'rgba(30, 41, 59, 0.5)'
                            }}>
                                <div style={{ 
                                    fontSize: '1rem', 
                                    fontWeight: 'bold', 
                                    marginBottom: '0.75rem',
                                    color: '#60a5fa',
                                    textAlign: 'center'
                                }}>
                                    {pkg}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                    {items.map(({ tier, count }, i) => (
                                        <div key={i} style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            padding: '0.25rem 0',
                                            borderBottom: i < items.length - 1 ? '1px solid #334155' : 'none'
                                        }}>
                                            <span>{tier} entries:</span>
                                            <span style={{ fontWeight: 'bold', color: '#10b981' }}>
                                                {count} giveaways
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{
                    padding: '1rem',
                    background: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '12px'
                }}>
                    <p style={{ marginBottom: '0.75rem', color: '#e2e8f0' }}>
                        <strong>📝 How to Purchase:</strong>
                    </p>
                    <ol style={{ marginLeft: '1.5rem', color: '#cbd5e1', lineHeight: '1.8' }}>
                        <li>Click the "Donate via Razorpay" button below</li>
                        <li>Send the amount you want to purchase: $1, $3, $5, or $10</li>
                        <li><strong>Important:</strong> Mention your username "<strong>{user?.username || 'your_username'}</strong>" in the payment message</li>
                        <li>Credits are calculated based on the net amount received after processing fees</li>
                        <li>Credits will be manually added to your account after payment is received (Can take upto 24 hours)</li>
                    </ol>
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                        <a 
                            href="https://razorpay.me/@alonelycat" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-block',
                                padding: '0.75rem 1.5rem',
                                background: '#3395FF',
                                color: '#fff',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            }}
                        >
                            💳 Donate via Razorpay
                        </a>
                    </div>
                </div>
            </div>

            {/* Verification Lookup Section */}
            <div className="tools-form-container" style={{ marginBottom: '2rem' }}>
                <div style={{ 
                    background: 'rgba(16, 185, 129, 0.05)', 
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: '12px',
                    padding: '1.5rem'
                }}>
                    <h3 style={{ color: '#10b981', marginBottom: '0.5rem', fontSize: '1.2rem' }}>
                        🔍 Verify a Giveaway Roll
                    </h3>
                    <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                        Enter a verification ID to check the authenticity of a giveaway roll and detect rerolls
                    </p>
                    
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            value={verificationId}
                            onChange={(e) => setVerificationId(e.target.value.toUpperCase())}
                            placeholder="Enter 12-character verification ID (e.g., A1B2C3D4E5F6)"
                            style={{
                                flex: '1',
                                minWidth: '250px',
                                padding: '0.75rem',
                                background: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '1rem',
                                fontFamily: 'monospace'
                            }}
                        />
                        <button
                            onClick={handleVerifyRoll}
                            disabled={verifyingRoll || !verificationId}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: verificationId ? '#10b981' : '#334155',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: verificationId ? 'pointer' : 'not-allowed',
                                fontWeight: '600',
                                fontSize: '1rem',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {verifyingRoll ? 'Verifying...' : '🔐 Verify'}
                        </button>
                    </div>

                    {verificationError && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '8px',
                            color: '#ef4444'
                        }}>
                            ❌ {verificationError}
                        </div>
                    )}

                    {verificationResult && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '1.5rem',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                <span style={{ fontSize: '1.5rem' }}>✅</span>
                                <h4 style={{ color: '#10b981', fontSize: '1.1rem', margin: 0 }}>Verification Successful</h4>
                                <a
                                    href={`/verify?id=${verificationResult.verificationId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        marginLeft: 'auto',
                                        padding: '0.25rem 0.75rem',
                                        background: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        textDecoration: 'none',
                                        fontWeight: '500'
                                    }}
                                >
                                    View Full Page →
                                </a>
                            </div>

                            {verificationResult.isReroll && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'rgba(251, 191, 36, 0.1)',
                                    border: '1px solid rgba(251, 191, 36, 0.3)',
                                    borderRadius: '8px',
                                    marginBottom: '1rem'
                                }}>
                                    <div style={{ color: '#fbbf24', fontWeight: '600', marginBottom: '0.5rem' }}>
                                        ⚠️ This giveaway has been rerolled
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                        Total rolls for this tweet: {verificationResult.rerollCount}
                                    </div>
                                    {verificationResult.rerollReason && (
                                        <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                                            <strong>Reason:</strong> {verificationResult.rerollReason}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Tweet ID</div>
                                    <div style={{ color: 'white', fontFamily: 'monospace', fontSize: '0.95rem' }}>
                                        {verificationResult.tweetId}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Rolled At</div>
                                    <div style={{ color: 'white', fontSize: '0.95rem' }}>
                                        {new Date(verificationResult.rolledAt).toLocaleString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Rolled By</div>
                                    <div style={{ color: 'white', fontSize: '0.95rem' }}>
                                        {verificationResult.rolledBy || 'Unknown'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Total Entries</div>
                                    <div style={{ color: 'white', fontSize: '0.95rem' }}>
                                        {verificationResult.totalRetweeters}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Winners ({verificationResult.winners.length})</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {verificationResult.winners.map((winner, idx) => (
                                        <div key={idx} style={{
                                            padding: '0.5rem 1rem',
                                            background: 'rgba(16, 185, 129, 0.2)',
                                            borderRadius: '8px',
                                            color: '#10b981',
                                            fontWeight: '600'
                                        }}>
                                            @{winner.username}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {verificationResult.rollHistory && verificationResult.rollHistory.length > 1 && (
                                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #334155' }}>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                                        Roll History for this Tweet ({verificationResult.rollHistory.length} total)
                                    </div>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {verificationResult.rollHistory.map((roll, idx) => (
                                            <div key={idx} style={{
                                                padding: '0.75rem',
                                                background: roll.verificationId === verificationResult.verificationId ? 'rgba(16, 185, 129, 0.15)' : 'rgba(30, 41, 59, 0.5)',
                                                border: `1px solid ${roll.verificationId === verificationResult.verificationId ? '#10b981' : '#334155'}`,
                                                borderRadius: '8px',
                                                fontSize: '0.85rem'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                    <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '600' }}>
                                                        {roll.verificationId}
                                                    </span>
                                                    <span style={{ color: '#64748b' }}>
                                                        {new Date(roll.rolledAt).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div style={{ color: '#94a3b8' }}>
                                                    Roll #{roll.rollNumber} • {roll.numberOfWinners} winner{roll.numberOfWinners > 1 ? 's' : ''} • By @{roll.rolledBy}
                                                </div>
                                                {roll.rerollReason && (
                                                    <div style={{ color: '#fbbf24', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                                        🔄 Rerolled: {roll.rerollReason}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="tools-form-container">
                <form onSubmit={handlePickWinner} className="tools-form">
                    <div className="form-group">
                        <label htmlFor="tweetId">
                            Tweet ID or URL *
                            <span className="label-hint">Paste tweet URL or just the ID</span>
                        </label>
                        <input
                            type="text"
                            id="tweetId"
                            onChange={handleTweetIdChange}
                            placeholder="https://x.com/user/status/1234567890 or https://twitter.com/user/status/1234567890"
                            required
                        />
                        {tweetId && (
                            <small className="extracted-id">Extracted ID: {tweetId}</small>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="expectedEntries">
                            Expected Number of Entries *
                            <span className="label-hint">How many retweets/entries do you expect? (exact number)</span>
                        </label>
                        <input
                            type="number"
                            id="expectedEntries"
                            value={expectedEntries}
                            onChange={(e) => {
                                setExpectedEntries(e.target.value);
                                setCreditCheck(null);
                            }}
                            min="1"
                            max="10000"
                            required
                            placeholder="e.g., 550"
                        />
                        {expectedEntries && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <button 
                                    type="button"
                                    onClick={handleCheckCredits}
                                    disabled={checkingCredits}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: checkingCredits ? 'not-allowed' : 'pointer',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    {checkingCredits ? 'Checking...' : 'Check Credits Required'}
                                </button>
                                {creditCheck && (
                                    <div style={{ 
                                        marginTop: '0.75rem', 
                                        padding: '0.75rem',
                                        background: creditCheck.hasEnough ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                        border: `1px solid ${creditCheck.hasEnough ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                        borderRadius: '8px'
                                    }}>
                                        <div style={{ color: creditCheck.hasEnough ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                                            {creditCheck.hasEnough ? '✓' : '✗'} {creditCheck.hasEnough ? 'You have enough credits!' : 'Insufficient credits'}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                            Required: {creditCheck.creditsNeeded.toLocaleString()} credits | 
                                            Your Balance: {creditCheck.currentBalance.toLocaleString()} credits |
                                            Cost: ${creditCheck.costUSD}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="numberOfWinners">
                            Number of Winners
                            <span className="label-hint">How many winners to pick?</span>
                        </label>
                        <input
                            type="number"
                            id="numberOfWinners"
                            value={numberOfWinners}
                            onChange={(e) => setNumberOfWinners(e.target.value)}
                            min="1"
                            max="100"
                            required
                        />
                    </div>

                    {/* Premium Filters */}
                    <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label>
                            Premium Engagement Filters
                            <span className="label-hint">These features require additional API calls and increase costs</span>
                        </label>
                        
                        {/* Warning about processing time */}
                        <div style={{
                            padding: '0.75rem',
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            borderRadius: '8px',
                            marginBottom: '0.75rem',
                            fontSize: '0.85rem',
                            color: '#fbbf24'
                        }}>
                            <strong>⏱️ Note:</strong> Premium features require checking each participant individually and will significantly increase processing time (several minutes for large giveaways).
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {/* Must Follow Section */}
                            <div style={{
                                padding: '0.75rem',
                                background: 'rgba(168, 85, 247, 0.05)',
                                border: '1px solid rgba(168, 85, 247, 0.2)',
                                borderRadius: '8px'
                            }}>
                                <div style={{ marginBottom: '0.75rem' }}>
                                    <strong style={{ color: '#a78bfa', fontSize: '0.95rem' }}>⚡ Must Follow Accounts</strong>
                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                        Add @handles that participants must follow
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                    {mustFollow.map((handle, index) => (
                                        <div key={index} style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.5rem 0.75rem',
                                            background: 'rgba(168, 85, 247, 0.2)',
                                            border: '1px solid rgba(168, 85, 247, 0.4)',
                                            borderRadius: '8px',
                                            color: '#a78bfa'
                                        }}>
                                            <span>@{handle}</span>
                                            <button
                                                type="button"
                                                onClick={() => setMustFollow(mustFollow.filter((_, i) => i !== index))}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#ef4444',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2rem',
                                                    padding: '0',
                                                    lineHeight: '1'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: mustFollow.length > 0 ? '0.75rem' : '0' }}>
                                    <input
                                        type="text"
                                        placeholder="@yourhandle"
                                        value={mustFollowInput}
                                        onChange={(e) => setMustFollowInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const cleanUsername = mustFollowInput.trim().replace(/^@/, '');
                                                if (cleanUsername && !mustFollow.includes(cleanUsername)) {
                                                    setMustFollow([...mustFollow, cleanUsername]);
                                                    setMustFollowInput('');
                                                }
                                            }
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem',
                                            background: '#1e293b',
                                            border: '1px solid #475569',
                                            borderRadius: '6px',
                                            color: 'white',
                                            fontSize: '0.9rem'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const cleanUsername = mustFollowInput.trim().replace(/^@/, '');
                                            if (cleanUsername && !mustFollow.includes(cleanUsername)) {
                                                setMustFollow([...mustFollow, cleanUsername]);
                                                setMustFollowInput('');
                                            }
                                        }}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: '#a78bfa',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Add
                                    </button>
                                </div>
                                
                                {mustFollow.length > 0 && (
                                    <label className="filter-checkbox" htmlFor="enforceMustFollow" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.75rem',
                                        background: 'rgba(168, 85, 247, 0.15)',
                                        border: '1px solid rgba(168, 85, 247, 0.4)',
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}>
                                        <input
                                            type="checkbox"
                                            id="enforceMustFollow"
                                            checked={enforceMustFollow}
                                            onChange={(e) => setEnforceMustFollow(e.target.checked)}
                                        />
                                        <span style={{ flex: 1 }}>
                                            <strong style={{ color: '#a78bfa' }}>Enforce Follow Check</strong>
                                            <span style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                                +{(mustFollow.length * 100).toLocaleString()} credits per entry
                                            </span>
                                        </span>
                                    </label>
                                )}
                            </div>
                            
                            <label className="filter-checkbox" htmlFor="requireReply" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem',
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    id="requireReply"
                                    checked={requireReply}
                                    onChange={(e) => setRequireReply(e.target.checked)}
                                />
                                <span style={{ flex: 1 }}>
                                    <strong style={{ color: '#4ade80' }}>💬 Must Reply to Tweet</strong>
                                    <span style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                        +15 credits per entry (~2x cost)
                                    </span>
                                </span>
                            </label>
                            
                            <label className="filter-checkbox" htmlFor="requireQuote" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.75rem',
                                background: 'rgba(245, 158, 11, 0.1)',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    id="requireQuote"
                                    checked={requireQuote}
                                    onChange={(e) => setRequireQuote(e.target.checked)}
                                />
                                <span style={{ flex: 1 }}>
                                    <strong style={{ color: '#fbbf24' }}>🔄 Must Quote Tweet</strong>
                                    <span style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                        +15 credits per entry (~2x cost)
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label>
                            Advanced Filters
                            <span className="label-hint">Optional filters to refine eligible winners</span>
                        </label>
                        <div className="filters-card">
                            <div className="filters-table">
                                <div className="filters-row">
                                    <div className="filters-label">Has profile picture</div>
                                    <div className="filters-control">
                                        <label className="filter-checkbox" htmlFor="requirePfp">
                                            <input type="checkbox" id="requirePfp" checked={requirePfp} onChange={(e) => setRequirePfp(e.target.checked)} />
                                            Enable
                                        </label>
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Has banner</div>
                                    <div className="filters-control">
                                        <label className="filter-checkbox" htmlFor="requireBanner">
                                            <input type="checkbox" id="requireBanner" checked={requireBanner} onChange={(e) => setRequireBanner(e.target.checked)} />
                                            Enable
                                        </label>
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Has location</div>
                                    <div className="filters-control">
                                        <label className="filter-checkbox" htmlFor="requireLocation">
                                            <input type="checkbox" id="requireLocation" checked={requireLocation} onChange={(e) => setRequireLocation(e.target.checked)} />
                                            Enable
                                        </label>
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Has description</div>
                                    <div className="filters-control">
                                        <label className="filter-checkbox" htmlFor="requireDescription">
                                            <input type="checkbox" id="requireDescription" checked={requireDescription} onChange={(e) => setRequireDescription(e.target.checked)} />
                                            Enable
                                        </label>
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Minimum tweet count</div>
                                    <div className="filters-control">
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="numeric"
                                            placeholder="e.g., 50"
                                            value={minTweetCount}
                                            onChange={(e) => setMinTweetCount(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Account older than (days)</div>
                                    <div className="filters-control">
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="numeric"
                                            placeholder="e.g., 180"
                                            value={accountOlderThanDays}
                                            onChange={(e) => setAccountOlderThanDays(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Latest tweet within (days)</div>
                                    <div className="filters-control">
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="numeric"
                                            placeholder="e.g., 30"
                                            value={latestTweetWithinDays}
                                            onChange={(e) => setLatestTweetWithinDays(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="filters-row">
                                    <div className="filters-label">Minimum followers</div>
                                    <div className="filters-control">
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="numeric"
                                            placeholder="e.g., 200"
                                            value={minFollowers}
                                            onChange={(e) => setMinFollowers(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <small className="filters-hint label-hint">
                            Filters are applied before winner selection and do not increase credit usage.
                        </small>
                    </div>

                    <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label>
                            🚫 Exclude Previous Winners
                            <span className="label-hint">Prevent same users from winning multiple times</span>
                        </label>
                        <div className="filters-card">
                            <div className="filters-table">
                                <div className="filters-row">
                                    <div className="filters-label">Exclude previous winners</div>
                                    <div className="filters-control">
                                        <label className="filter-checkbox" htmlFor="excludePreviousWinners">
                                            <input 
                                                type="checkbox" 
                                                id="excludePreviousWinners" 
                                                checked={excludePreviousWinners} 
                                                onChange={(e) => setExcludePreviousWinners(e.target.checked)} 
                                            />
                                            Enable
                                        </label>
                                    </div>
                                </div>
                                {excludePreviousWinners && (
                                    <div className="filters-row">
                                        <div className="filters-label">Exclude winners from</div>
                                        <div className="filters-control">
                                            <select 
                                                value={excludeWinnersDays} 
                                                onChange={(e) => setExcludeWinnersDays(e.target.value)}
                                                style={{
                                                    padding: '0.5rem',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--border-color)',
                                                    background: 'var(--card-bg)',
                                                    color: 'var(--text-primary)',
                                                    fontSize: '0.9rem',
                                                    width: '100%'
                                                }}
                                            >
                                                <option value={1}>Last 1 day</option>
                                                <option value={3}>Last 3 days</option>
                                                <option value={5}>Last 5 days</option>
                                                <option value={7}>Last 7 days</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <small className="filters-hint label-hint">
                            {excludePreviousWinners 
                                ? `Winners from your giveaways in the last ${excludeWinnersDays} day(s) will be automatically excluded.`
                                : 'Enable to prevent the same people from winning multiple times in your giveaways.'}
                        </small>
                    </div>

                    <button 
                        type="submit" 
                        className="tools-btn" 
                        disabled={loading || !creditCheck || !creditCheck.hasEnough}
                    >
                        {loading ? 'Picking Winners...' : 'Pick Winners'}
                    </button>
                    {!isAuthenticated && (
                        <p style={{ textAlign: 'center', color: '#ef4444', marginTop: '0.5rem' }}>
                            Please login to use this feature
                        </p>
                    )}
                </form>

                <div className="info-notice" style={{
                    marginTop: '1.5rem',
                    padding: '1rem 1.5rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <span style={{ fontSize: '2rem' }}>⚠️</span>
                    <small style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        <strong>Performance Notice:</strong> To ensure fairness, we fetch EVERY retweeter.
                        With optimized 20 QPS rate limiting, large giveaways (1k+ entries) typically complete in <strong>10-30 seconds</strong>.
                        Please don't refresh the page while the spinner is active.
                    </small>
                </div>

                {error && (
                    <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                    </div>
                )}
            </div>

            {/* Results Section - Winners appear first */}
            {result && (
                <div className="results-container">
                    {/* Stats Summary */}
                    <div className="results-section stats-summary">
                        <h2>📊 Contest Statistics</h2>
                        <div className="stats-grid-compact">
                            <div className="stat-card-compact">
                                <div className="stat-icon">🔄</div>
                                <div className="stat-info">
                                    <div className="stat-value">{(result.filteredRetweetersCount ?? result.totalRetweeters).toLocaleString?.() || (result.filteredRetweetersCount ?? result.totalRetweeters)}</div>
                                    <div className="stat-label">Eligible Participants</div>
                                </div>
                            </div>
                            {typeof result.originalRetweetersCount === 'number' && (
                                <div className="stat-card-compact">
                                    <div className="stat-icon">📥</div>
                                    <div className="stat-info">
                                        <div className="stat-value">{result.originalRetweetersCount.toLocaleString()}</div>
                                        <div className="stat-label">Total Retweets Fetched</div>
                                    </div>
                                </div>
                            )}
                            {typeof result.excludedWinnersCount === 'number' && result.excludedWinnersCount > 0 && (
                                <div className="stat-card-compact">
                                    <div className="stat-icon">🚫</div>
                                    <div className="stat-info">
                                        <div className="stat-value">{result.excludedWinnersCount.toLocaleString()}</div>
                                        <div className="stat-label">Previous Winners Excluded</div>
                                    </div>
                                </div>
                            )}
                            <div className="stat-card-compact">
                                <div className="stat-icon">🏆</div>
                                <div className="stat-info">
                                    <div className="stat-value">{result.numberOfWinners}</div>
                                    <div className="stat-label">Winners Selected</div>
                                </div>
                            </div>
                            <div className="stat-card-compact">
                                <div className="stat-icon">📅</div>
                                <div className="stat-info">
                                    <div className="stat-value">{new Date(result.pickedAt).toLocaleDateString()}</div>
                                    <div className="stat-label">Draw Date</div>
                                </div>
                            </div>
                            {result.creditsUsed !== undefined && (
                                <div className="stat-card-compact">
                                    <div className="stat-icon">💳</div>
                                    <div className="stat-info">
                                        <div className="stat-value">{result.creditsUsed.toLocaleString()}</div>
                                        <div className="stat-label">{result.fromCache ? 'Credits Used (Cached)' : 'Credits Used'}</div>
                                    </div>
                                </div>
                            )}
                            {result.remainingCredits !== undefined && (
                                <div className="stat-card-compact">
                                    <div className="stat-icon">💰</div>
                                    <div className="stat-info">
                                        <div className="stat-value">{result.remainingCredits.toLocaleString()}</div>
                                        <div className="stat-label">Remaining Credits</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Verification ID */}
                        {result.verificationId && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                background: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '0.5rem'
                            }}>
                                <div>
                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                                        🔐 Verification ID (for transparency)
                                    </div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10b981', fontFamily: 'monospace' }}>
                                        {result.verificationId}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(result.verificationId);
                                        toast.success('Verification ID copied to clipboard!');
                                    }}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: '600'
                                    }}
                                >
                                    📋 Copy ID
                                </button>
                            </div>
                        )}
                        
                        {/* Exclude Previous Winners Notice */}
                        {result.excludePreviousWinners && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}>
                                <span style={{ fontSize: '1.5rem' }}>🚫</span>
                                <div>
                                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                                        Previous Winners Excluded
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                                        {result.excludedWinnersCount > 0 
                                            ? `${result.excludedWinnersCount} user${result.excludedWinnersCount !== 1 ? 's' : ''} who won in your last ${result.excludeWinnersDays} day${result.excludeWinnersDays !== 1 ? 's' : ''} ${result.excludedWinnersCount !== 1 ? 'were' : 'was'} automatically excluded from this draw.`
                                            : `No previous winners found in the last ${result.excludeWinnersDays} day${result.excludeWinnersDays !== 1 ? 's' : ''}. All eligible participants were included.`
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Winners List */}
                    {result.winners && result.winners.length > 0 ? (
                        <div className="results-section winners-section">
                            <div className="winners-header">
                                <h2>🎊 Congratulations to the Winners!</h2>
                                <button className="copy-winners-btn" onClick={copyWinners}>
                                    📋 Copy Winners List
                                </button>
                            </div>

                            <div className="winners-grid">
                                {result.winners.map((winner, index) => (
                                    <div key={winner.userId} className="winner-card">
                                        <div className="winner-header-premium">
                                            {winner.profileImageUrl && (
                                                <img
                                                    src={winner.profileImageUrl}
                                                    alt={winner.displayName}
                                                    className="winner-avatar-premium"
                                                />
                                            )}
                                            <div className="winner-info-premium">
                                                <h3 className="winner-name-premium">
                                                    {winner.displayName}
                                                    {winner.isVerified && (
                                                        <span className="verified-icon" title="Verified Account">✓</span>
                                                    )}
                                                </h3>
                                                <span className="winner-handle-premium">@{winner.username}</span>
                                            </div>
                                        </div>

                                        {/* User Stats */}
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '0.5rem',
                                            padding: '0.75rem',
                                            background: 'rgba(30, 41, 59, 0.5)',
                                            borderRadius: '8px',
                                            marginTop: '0.75rem'
                                        }}>
                                            {winner.followers !== undefined && (
                                                <div style={{ fontSize: '0.85rem' }}>
                                                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Followers</div>
                                                    <div style={{ color: '#e2e8f0', fontWeight: '600' }}>{winner.followers?.toLocaleString() || 0}</div>
                                                </div>
                                            )}
                                            {winner.tweetsCount !== undefined && (
                                                <div style={{ fontSize: '0.85rem' }}>
                                                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Tweets</div>
                                                    <div style={{ color: '#e2e8f0', fontWeight: '600' }}>{winner.tweetsCount?.toLocaleString() || 0}</div>
                                                </div>
                                            )}
                                            {winner.accountAgeDays !== undefined && winner.accountAgeDays !== null && (
                                                <div style={{ fontSize: '0.85rem' }}>
                                                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Account Age</div>
                                                    <div style={{ color: '#e2e8f0', fontWeight: '600' }}>{Math.floor(winner.accountAgeDays / 365)}y {Math.floor((winner.accountAgeDays % 365) / 30)}m</div>
                                                </div>
                                            )}
                                            {winner.location && (
                                                <div style={{ fontSize: '0.85rem' }}>
                                                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Location</div>
                                                    <div style={{ color: '#e2e8f0', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{winner.location}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Description if available */}
                                        {winner.description && (
                                            <div style={{
                                                padding: '0.75rem',
                                                background: 'rgba(30, 41, 59, 0.3)',
                                                borderRadius: '8px',
                                                marginTop: '0.5rem',
                                                fontSize: '0.85rem',
                                                color: '#cbd5e1',
                                                fontStyle: 'italic',
                                                maxHeight: '3em',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                "{winner.description}"
                                            </div>
                                        )}

                                        <a
                                            href={`https://x.com/${winner.username}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'block',
                                                marginTop: '0.75rem',
                                                padding: '0.5rem',
                                                background: 'rgba(29, 155, 240, 0.1)',
                                                border: '1px solid rgba(29, 155, 240, 0.3)',
                                                borderRadius: '8px',
                                                color: '#1d9bf0',
                                                textDecoration: 'none',
                                                textAlign: 'center',
                                                fontSize: '0.85rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            🔗 View Profile on X
                                        </a>

                                        <a
                                            href={`https://x.com/i/web/status/${tweetId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="winning-tweet-badge"
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <span className="winning-tweet-icon">🐦</span>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Winning Tweet</span>
                                                <span className="winning-tweet-text">View Giveaway Tweet</span>
                                            </div>
                                        </a>

                                        <div className="giveaway-info-premium">
                                            <div className="verified-draw-badge">
                                                <span>🛡️</span> Verified Draw
                                            </div>
                                            <span>{new Date(result.pickedAt).toLocaleDateString()}</span>
                                        </div>

                                        <button
                                            className="share-x-btn"
                                            onClick={() => {
                                                const text = `🎉 Congrats @${winner.username} for winning the giveaway! \n\n✅ Verified draw by GamersUnion\n\nSee results here:`;
                                                const url = window.location.href;
                                                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                                            }}
                                        >
                                            <span>𝕏</span> Share Result
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Reroll Button */}
                            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                                <button
                                    onClick={() => setShowRerollModal(true)}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: '#f59e0b',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    🔄 Reroll Giveaway
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="results-section no-winners">
                            <h2>😔 No Retweets Found</h2>
                            <p>This tweet doesn't have any retweets yet. Make sure the tweet ID is correct and the tweet has been retweeted.</p>
                        </div>
                    )}

                    <div className="results-footer">
                        <small>
                            ℹ️ Winners are selected randomly from all retweeters. Each user has an equal chance of winning.
                            <br />
                            Draw performed on: {new Date(result.pickedAt).toLocaleString()}
                        </small>
                    </div>
                </div>
            )}

            {/* Reroll Modal */}
            {showRerollModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'var(--card-bg, #1e293b)',
                        borderRadius: '16px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        border: '1px solid var(--border-color, #334155)'
                    }}>
                        <h3 style={{ color: '#e2e8f0', marginBottom: '1rem' }}>🔄 Reroll Giveaway</h3>
                        <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>
                            This will select new winners from the same tweet. Please provide a reason for rerolling.
                        </p>
                        
                        <form onSubmit={handleReroll}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ 
                                    display: 'block', 
                                    color: '#cbd5e1', 
                                    marginBottom: '0.5rem',
                                    fontWeight: '600'
                                }}>
                                    Reason for Rerolling *
                                </label>
                                <textarea
                                    value={rerollReason}
                                    onChange={(e) => setRerollReason(e.target.value)}
                                    placeholder="e.g., Winner didn't respond within 24 hours"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        background: '#1e293b',
                                        border: '1px solid #334155',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '1rem',
                                        minHeight: '80px',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                            
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowRerollModal(false);
                                        setRerollReason('');
                                        setError('');
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: '#6b7280',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '1rem'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={rerolling || !rerollReason.trim()}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: rerolling || !rerollReason.trim() ? '#6b7280' : '#f59e0b',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: rerolling || !rerollReason.trim() ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: '600'
                                    }}
                                >
                                    {rerolling ? '🔄 Rerolling...' : '🔄 Reroll'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Credit Usage Chart Section */}
            <div className="chart-container" style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'var(--card-bg, #1e293b)',
                borderRadius: '16px',
                border: '1px solid var(--border-color, #334155)'
            }}>
                <Bar options={chartOptions} data={chartData} />
                <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.9rem', color: '#94a3b8' }}>
                    <p>* Costs are estimated based on API usage per entry volume.</p>
                </div>
            </div>

            <div className="info-section">
                <h2>ℹ️ How to Use</h2>
                <div className="info-grid">
                    <div className="info-card">
                        <h3>🔍 Get Tweet ID</h3>
                        <p>
                            Copy the full tweet URL from your browser or mobile app. You can also just use the numerical ID
                            from the URL (the numbers after /status/). Both formats work!
                        </p>
                    </div>
                    <div className="info-card">
                        <h3>🎲 Fair Selection</h3>
                        <p>
                            Winners are selected using a cryptographically secure random algorithm. Every retweeter
                            has an equal chance of winning, ensuring a fair and transparent selection process.
                        </p>
                    </div>
                    <div className="info-card">
                        <h3>⚡ Quick & Easy</h3>
                        <p>
                            Perfect for giveaways, contests, and community engagement. Select multiple winners at once,
                            and easily copy the winners list to announce on your social media.
                        </p>
                    </div>
                </div>

                <div className="disclaimer-box">
                    <div className="disclaimer-icon">⚠️</div>
                    <div className="disclaimer-content">
                        <h4>Important Notes</h4>
                        <ul>
                            <li>Make sure the tweet is public and has retweets.</li>
                            <li>The API can fetch up to 3,000 retweeters (with automatic pagination).</li>
                            <li>Results are 100% random and cryptographically secure.</li>
                            <li>Save or screenshot your results for record-keeping.</li>
                        </ul>
                    </div>
                </div>

                <div className="disclaimer-box" style={{ marginTop: '2rem', borderLeftColor: '#ef4444' }}>
                    <div className="disclaimer-icon">❓</div>
                    <div className="disclaimer-content">
                        <h4>Why is the count different?</h4>
                        <p style={{ marginBottom: '1rem', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                            Sometimes you'll see more retweets in the Twitter/X UI than the tool picks up.
                            This is because the API cannot access:
                        </p>
                        <ul className="disclaimer-list-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '0.5rem',
                            listStyle: 'none',
                            padding: 0
                        }}>
                            <li>🔒 <strong>Protected</strong> (private accounts)</li>
                            <li>🚫 <strong>Suspended</strong> accounts</li>
                            <li>🕵️ <strong>Restricted</strong> / Shadow-limited</li>
                            <li>🗑️ <strong>Deleted</strong> after Retweeting</li>
                            <li>🛑 <strong>Blocked</strong> you</li>
                            <li>🤐 <strong>Soft-banned</strong> accounts</li>
                        </ul>
                        <p style={{ marginTop: '1rem', fontSize: '0.9rem', fontStyle: 'italic', opacity: 0.8 }}>
                            The tool only picks from retweeters that are publicly visible to the API at the moment of the draw.
                        </p>
                    </div>
                </div>
            </div>

            {/* Gift Credits Modal */}
            {showGiftModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: 'var(--card-bg, #1e293b)',
                        borderRadius: '16px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '100%',
                        border: '1px solid var(--border-color, #334155)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1.5rem'
                        }}>
                            <h2 style={{ 
                                margin: 0, 
                                color: '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '1.5rem'
                            }}>
                                🎁 Gift Credits
                            </h2>
                            <button
                                onClick={() => {
                                    setShowGiftModal(false);
                                    setGiftMessage({ type: '', text: '' });
                                    setGiftRecipient('');
                                    setGiftAmount('');
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: '1.5rem',
                                    padding: '0.25rem',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(148, 163, 184, 0.1)'}
                                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={handleGiftCredits}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ 
                                    display: 'block', 
                                    color: '#e2e8f0', 
                                    fontSize: '1rem',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500'
                                }}>
                                    Select Recipient
                                </label>
                                <UserSearchDropdown
                                    value={giftRecipient}
                                    onChange={setGiftRecipient}
                                    placeholder="Search for a user..."
                                />
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ 
                                    display: 'block', 
                                    color: '#e2e8f0', 
                                    fontSize: '1rem',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500'
                                }}>
                                    Credits to Gift
                                </label>
                                <input
                                    type="number"
                                    value={giftAmount}
                                    onChange={(e) => setGiftAmount(e.target.value)}
                                    placeholder="Enter amount..."
                                    min="1"
                                    max={credits}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        backgroundColor: 'var(--input-bg, #0f172a)',
                                        border: '1px solid var(--border-color, #334155)',
                                        borderRadius: '8px',
                                        color: '#e2e8f0',
                                        fontSize: '1rem',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                <p style={{ 
                                    fontSize: '0.85rem', 
                                    color: '#94a3b8', 
                                    marginTop: '0.25rem',
                                    marginBottom: 0
                                }}>
                                    Available: {credits.toLocaleString()} credits
                                </p>
                            </div>

                            {giftMessage.text && (
                                <div style={{
                                    padding: '0.75rem 1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1.5rem',
                                    backgroundColor: giftMessage.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    border: `1px solid ${giftMessage.type === 'success' ? '#10b981' : '#ef4444'}`,
                                    color: giftMessage.type === 'success' ? '#10b981' : '#ef4444'
                                }}>
                                    {giftMessage.text}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowGiftModal(false);
                                        setGiftMessage({ type: '', text: '' });
                                        setGiftRecipient('');
                                        setGiftAmount('');
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: 'transparent',
                                        color: '#94a3b8',
                                        border: '1px solid #475569',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={giftingCredits}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: giftingCredits ? '#64748b' : '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: giftingCredits ? 'not-allowed' : 'pointer',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    {giftingCredits ? '🎁 Gifting...' : '🎁 Gift Credits'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TwitterWinnerPicker;
